<template>
    <div>
        <DataTable
            :columns="tableColumns"
            :items="items"
            :current-page="currentPage"
            :total-pages="totalPages"
            :enable-multi-select="false"
            :enable-drag-drop="false"
            @page-changed="handlePageChange"
            @sort="onSort"
        >
            <template #toolbar>
                <div class="ppv2-flex ppv2-flex-wrap ppv2-items-center" :style="{ gap: normalizeSize(16, true) }">
                    <div class="ppv2-flex-1"></div>

                    <!-- Export Select -->
                    <button
                        @click="handleExport"
                        class="ppv2-flex ppv2-items-center ppv2-gap-3 ppv2-px-4 ppv2-py-3 ppv2-rounded-xl ppv2-border ppv2-border-solid ppv2-border-black ppv2-text-black hover:ppv2-bg-opacity-80 ppv2-transition-all"
                        :disabled="exportLoading"
                    >
                        <svg
                            v-if="exportLoading"
                            class="ppv2-w-5 ppv2-h-5 ppv2-animate-spin"
                            xmlns="http://www.w3.org/2000/svg"
                            fill="none"
                            viewBox="0 0 24 24"
                        >
                            <circle
                                class="ppv2-opacity-25"
                                cx="12"
                                cy="12"
                                r="10"
                                stroke="currentColor"
                                stroke-width="4"
                            ></circle>
                            <path
                                class="ppv2-opacity-75"
                                fill="currentColor"
                                d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                            ></path>
                        </svg>
                        <svg v-else width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1.21289 17.6012C1.21289 14.7752 2.69884 12.2962 4.93205 10.9029C5.53943 6.12426 9.62017 2.42969 14.5638 2.42969C19.5074 2.42969 23.5882 6.12426 24.1956 10.9029C26.4288 12.2962 27.9148 14.7752 27.9148 17.6012C27.9148 21.7541 24.7059 25.1575 20.6324 25.4675L8.49522 25.4904C4.42171 25.1575 1.21289 21.7541 1.21289 17.6012ZM20.4483 23.0469C23.2804 22.8315 25.4873 20.4609 25.4873 17.6012C25.4873 15.692 24.5023 13.9554 22.9107 12.9625L21.9328 12.3524L21.7875 11.209C21.3285 7.59775 18.24 4.85713 14.5638 4.85713C10.8877 4.85713 7.79912 7.59775 7.34012 11.209L7.19479 12.3524L6.21692 12.9625C4.62534 13.9554 3.64034 15.692 3.64034 17.6012C3.64034 20.4609 5.84728 22.8315 8.67935 23.0469L8.88968 23.063H20.238L20.4483 23.0469ZM15.7776 14.5669H19.4187L14.5638 20.6355L9.70895 14.5669H13.3501V9.71202H15.7776V14.5669Z"
                                fill="currentColor"
                            />
                        </svg>
                        <span class="poppinsRegular" :style="{ fontSize: normalizeFontSize(16) }">
                            {{ exportLoading ? $t('voucher.export') + '...' : $t('voucher.export') }}
                        </span>
                    </button>
                    <!-- Search Input -->
                    <div class="ppv2-relative">
                        <Search v-model="searchQuery" :emitGlobal="false" @search-updated="onSearch" />
                    </div>
                </div>
            </template>

            <template #column-status="{ item }">
                <span :class="getStatusClass(item.status)">{{ getStatusLabel(item.status) }}</span>
            </template>

            <template #column-actions="{ item }">
                <div
                    class="ppv2-flex ppv2-items-center ppv2-justify-center ppv2-gap-3"
                    :style="{ fontSize: normalizeFontSize(18) }"
                >
                    <!-- Edit -->
                    <a
                        :href="getEditUrl(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('voucherDatatable.actions.edit')"
                    >
                        <SquarePen :size="18" />
                    </a>

                    <!-- Invoice -->
                    <a
                        v-if="item.order_item"
                        :href="getInvoiceUrl(item.order_item.order.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('voucherDatatable.actions.generate_invoice')"
                    >
                        <FileText :size="18" />
                    </a>

                    <!-- Toggle Status -->
                    <button
                        @click="setSelectedVoucher(item.id, item.is_blocked ? 'enable' : 'disable')"
                        class="ppv2-p-1 hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :class="
                            item.is_blocked
                                ? 'ppv2-text-green-600 hover:ppv2-text-green-600 hover:ppv2-bg-green-600'
                                : 'ppv2-text-[#FF9C9C] hover:ppv2-text-[#FF9C9C] hover:ppv2-bg-[#FF9C9C]'
                        "
                        :title="
                            item.is_blocked ? $t('voucherDatatable.actions.reactivate') : $t('voucherDatatable.actions.disable')
                        "
                    >
                        <CheckCircle v-if="item.is_blocked" :size="18" />
                        <Ban v-else :size="18" />
                    </button>

                    <!-- History -->
                    <a
                        :href="getHistoryUrl(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('voucherDatatable.actions.history')"
                    >
                        <History :size="18" />
                    </a>

                    <!-- Send Email -->
                    <a
                        :href="getSendUrl(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('voucherDatatable.actions.resend_email')"
                    >
                        <Mail :size="18" />
                    </a>

                    <!-- Download -->
                    <a
                        :href="getDownloadUrl(item.id)"
                        class="ppv2-p-1 ppv2-text-black hover:ppv2-text-black hover:ppv2-bg-black hover:ppv2-bg-opacity-10 ppv2-rounded ppv2-transition-all"
                        :title="$t('voucherDatatable.actions.download')"
                    >
                        <svg width="18" height="18" viewBox="0 0 18 18" fill="none" xmlns="http://www.w3.org/2000/svg">
                            <path
                                d="M1.21289 17.6012C1.21289 14.7752 2.69884 12.2962 4.93205 10.9029C5.53943 6.12426 9.62017 2.42969 14.5638 2.42969C19.5074 2.42969 23.5882 6.12426 24.1956 10.9029C26.4288 12.2962 27.9148 14.7752 27.9148 17.6012C27.9148 21.7541 24.7059 25.1575 20.6324 25.4675L8.49522 25.4904C4.42171 25.1575 1.21289 21.7541 1.21289 17.6012ZM20.4483 23.0469C23.2804 22.8315 25.4873 20.4609 25.4873 17.6012C25.4873 15.692 24.5023 13.9554 22.9107 12.9625L21.9328 12.3524L21.7875 11.209C21.3285 7.59775 18.24 4.85713 14.5638 4.85713C10.8877 4.85713 7.79912 7.59775 7.34012 11.209L7.19479 12.3524L6.21692 12.9625C4.62534 13.9554 3.64034 15.692 3.64034 17.6012C3.64034 20.4609 5.84728 22.8315 8.67935 23.0469L8.88968 23.063H20.238L20.4483 23.0469ZM15.7776 14.5669H19.4187L14.5638 20.6355L9.70895 14.5669H13.3501V9.71202H15.7776V14.5669Z"
                                fill="currentColor"
                            />
                        </svg>
                    </a>
                </div>
            </template>
        </DataTable>

        <!-- Toggle Voucher Modal Component -->
        <VoucherToggleModal
            modal-id="modal-toggle-voucher"
            :title="modalTitle"
            :confirm-text="modalButtonText"
            :cancel-text="$t('voucherDatatable.modal.cancel')"
            :loading="isLoading"
            :show="showModal"
            @confirm="confirmToggleStatus"
            @cancel="closeModal"
            @close="closeModal"
        />
    </div>
</template>

<script>
import DataTable from '../../Shared/DataTable.vue'
import ExportSelect from '../../Shared/ExportSelect.vue'
import VoucherToggleModal from './VoucherToggleModal.vue'
import { SquarePen, FileText, Ban, CheckCircle, History, Mail, CloudDownload } from 'lucide-vue'
import normalizeSize from '../../../utils/normalizeSize'
import Search from '../../V3/Experience/components/Search.vue'
import normalizeFontSize from '../../../utils/normalizeFontSize'

export default {
    name: 'VoucherDatatableV2',
    components: {
        DataTable,
        ExportSelect,
        VoucherToggleModal,
        SquarePen,
        FileText,
        Ban,
        CheckCircle,
        History,
        Mail,
        CloudDownload,
        Search,
    },
    data() {
        return {
            items: [],
            loading: false,
            currentPage: 1,
            perPage: 10,
            totalEntries: 0,
            searchQuery: '',
            sortKey: '',
            sortOrder: 'asc',
            searchTimeout: null,
            exportLoading: false,

            selectedVoucherId: null,
            selectedAction: null,
            isLoading: false,
            showModal: false,

            tableColumns: [
                {
                    key: 'code',
                    label: this.$t('voucherDatatable.columns.code'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'send_date',
                    label: this.$t('voucherDatatable.columns.send_date'),
                    sortable: true,
                    formatter: (value) => {
                        return value ? new Date(value).toLocaleString('fr-FR') : '-'
                    },
                },
                {
                    key: 'date_end',
                    label: this.$t('voucherDatatable.columns.date_end'),
                    sortable: true,
                    formatter: (value) => {
                        return value ? new Date(value).toLocaleString('fr-FR') : '-'
                    },
                    type: 'date',
                },
                {
                    key: 'total_amount',
                    label: this.$t('voucherDatatable.columns.total_amount'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'available_amount',
                    label: this.$t('voucherDatatable.columns.available_amount'),
                    sortable: true,
                    type: 'text',
                },
                {
                    key: 'status',
                    label: this.$t('voucherDatatable.columns.status.placeholder'),
                    sortable: false,
                    type: 'badge',
                },
                {
                    key: 'created_at',
                    label: this.$t('voucherDatatable.columns.created_at'),
                    sortable: true,
                    formatter: (value) => {
                        return value ? new Date(value).toLocaleString('fr-FR') : '-'
                    },
                    type: 'date',
                },
                {
                    key: 'actions',
                    label: this.$t('voucherDatatable.columns.actions'),
                    sortable: false,
                    type: 'actions',
                },
            ],
            apiHeaders: {
                'X-Requested-With': 'XMLHttpRequest',
            },
        }
    },
    computed: {
        apiUrl() {
            return `${window.location.origin}/back-office/bons_cadeau-v2/ajax`
        },
        totalPages() {
            return Math.ceil(this.totalEntries / this.perPage)
        },
        csrfToken() {
            return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
        },
        modalTitle() {
            return this.selectedAction === 'enable'
                ? this.$t('voucherDatatable.modal.title.reactivate')
                : this.$t('voucherDatatable.modal.title.disable')
        },
        modalButtonText() {
            return this.selectedAction === 'enable'
                ? this.$t('voucherDatatable.modal.button.reactivate')
                : this.$t('voucherDatatable.modal.button.disable')
        },
    },
    mounted() {
        this.fetchData()
    },
    methods: {
        normalizeFontSize,
        normalizeSize,
        async fetchData() {
            this.loading = true
            try {
                const params = {
                    page: this.currentPage,
                    per_page: this.perPage,
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                    columns: this.tableColumns.map((col) => col.key),
                }
                const response = await this.makeApiRequest(params)
                this.handleApiResponse(response)
            } catch (error) {
                this.$toast.error(error.response.data.message)
                this.onError(error)
            } finally {
                this.loading = false
            }
        },
        async makeApiRequest(params) {
            const queryString = new URLSearchParams(params).toString()
            const url = `${this.apiUrl}?${queryString}`

            const response = await fetch(url, {
                method: 'GET',
                headers: {
                    'Content-Type': 'application/json',
                    ...this.apiHeaders,
                },
            })

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`)
            }

            return await response.json()
        },
        handleApiResponse(response) {
            let _response = response.data
            if (_response.data && Array.isArray(_response.data)) {
                this.items = _response.data
                this.totalEntries = _response.total || _response.data.length
            } else if (Array.isArray(_response)) {
                this.items = _response
                this.totalEntries = _response.length
            } else {
                this.items = []
                this.totalEntries = 0
            }
        },
        handlePageChange(page) {
            this.currentPage = page
            this.fetchData()
        },
        onSearch() {
            clearTimeout(this.searchTimeout)
            this.searchTimeout = setTimeout(() => {
                this.currentPage = 1
                this.fetchData()
            }, 500)
        },
        onSort(key) {
            if (this.sortKey === key) {
                this.sortOrder = this.sortOrder === 'asc' ? 'desc' : 'asc'
            } else {
                this.sortKey = key
                this.sortOrder = 'asc'
            }
            this.currentPage = 1
            this.fetchData()
        },
        onError(error) {
            console.error('Datatable error:', error)
            this.$toast.error(error.message || this.$t('voucherDatatable.notifications.loadError'))
        },
        getStatusClass(status) {
            const classes = {
                active: 'badge bg-success text-white rounded-pill',
                blocked: 'badge bg-danger text-white rounded-pill',
                partially_used: 'badge bg-warning text-white rounded-pill',
                expired: 'badge bg-secondary text-white rounded-pill',
                used: 'badge bg-info text-white rounded-pill',
            }
            return classes[status] || 'badge bg-secondary text-white rounded-pill'
        },
        getStatusLabel(status) {
            return this.$t(`voucherDatatable.columns.status.options.${status}`) || status
        },
        getEditUrl(id) {
            return `/back-office/bons_cadeau/${id}/edit`
        },
        getInvoiceUrl(orderId) {
            return `/back-office/invoice/${orderId}`
        },
        getHistoryUrl(id) {
            return `/back-office/bons_cadeau/${id}/history`
        },
        getSendUrl(id) {
            return `/back-office/bons_cadeau/${id}/send_voucher`
        },
        getDownloadUrl(id) {
            return `/back-office/bons_cadeau-v2/${id}`
        },
        setSelectedVoucher(id, action) {
            this.selectedVoucherId = id
            this.selectedAction = action
            this.showModal = true
        },
        closeModal() {
            this.showModal = false
            this.selectedVoucherId = null
            this.selectedAction = null
        },
        updateRowStatus(voucherId, action) {
            const rowIndex = this.items.findIndex((row) => row.id === voucherId)
            if (rowIndex !== -1) {
                this.items[rowIndex].is_blocked = action === 'disable'
            }
        },
        async confirmToggleStatus() {
            if (!this.selectedVoucherId || !this.selectedAction) return

            this.isLoading = true
            try {
                // Using fetch instead of this.$http.post to avoid dependency on global axios/http if not available
                // But keeping the logic similar to original file
                const response = await fetch(`/back-office/bons_cadeau-v2/${this.selectedVoucherId}/toggle-status`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        'X-CSRF-TOKEN': this.csrfToken,
                        'X-Requested-With': 'XMLHttpRequest',
                    },
                    body: JSON.stringify({
                        action: this.selectedAction,
                    }),
                })

                const data = await response.json()

                if (data.success) {
                    this.$toast.success(data.message)

                    this.updateRowStatus(this.selectedVoucherId, this.selectedAction)
                    this.closeModal()
                } else {
                    throw new Error(data.message || this.$t('common.error'))
                }
            } catch (error) {
                this.$toast.error(error.message || this.$t('common.error'))
            } finally {
                this.isLoading = false
            }
        },
        async handleExport() {
            this.exportLoading = true
            try {
                const params = new URLSearchParams({
                    export: '1',
                    search: this.searchQuery,
                    sort_by: this.sortKey,
                    sort_order: this.sortOrder,
                })

                const link = document.createElement('a')
                link.href = `${this.apiUrl}?${params.toString()}`
                link.download = `bons_cadeaux_${new Date().toISOString().split('T')[0]}.xlsx`
                document.body.appendChild(link)
                link.click()
                document.body.removeChild(link)

                this.$toast.success(this.$t('voucherDatatable.notifications.exportSuccess'))
            } catch (error) {
                this.$toast.error(error.response.data.message)
            } finally {
                this.exportLoading = false
            }
        },
    },
}
</script>

<style scoped>
.badge.bg-success {
    background-color: #33df6f !important;
}

.badge.bg-danger {
    background-color: #ff2424 !important;
}

.badge.bg-info {
    background-color: #0094fb96 !important;
}
</style>
